import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

function Network() {
  const { fetchNetworkGraph } = useApp();
  const [network, setNetwork] = useState({ contacts: [], connections: [] });
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const loadNetwork = async () => {
      const data = await fetchNetworkGraph();
      setNetwork(data);

      // Initialize positions in a circular layout
      const newPositions = {};
      const centerX = 400;
      const centerY = 300;
      const radius = Math.min(250, 50 + data.contacts.length * 20);

      data.contacts.forEach((contact, index) => {
        const angle = (2 * Math.PI * index) / data.contacts.length;
        newPositions[contact.id] = {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle)
        };
      });

      setPositions(newPositions);
      setLoading(false);
    };
    loadNetwork();
  }, []); // Only fetch once on mount

  const handleMouseDown = useCallback((e, contactId) => {
    e.preventDefault();
    setDragging(contactId);
    setSelected(contactId);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    setPositions(prev => ({
      ...prev,
      [dragging]: { x, y }
    }));
  }, [dragging, zoom]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  const getCategoryColor = (category) => {
    const colors = {
      family: '#ef4444',
      friends: '#22c55e',
      professional: '#3b82f6',
      acquaintance: '#6b7280'
    };
    return colors[category] || '#6b7280';
  };

  const selectedContact = selected ? network.contacts.find(c => c.id === selected) : null;
  const selectedConnections = selected
    ? network.connections.filter(c => c.contact_id_1 === selected || c.contact_id_2 === selected)
    : [];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
          Network Map
        </h1>
        <p className="text-sm text-muted">
          Visualize connections between your contacts
        </p>
      </div>

      {network.contacts.length === 0 ? (
        <div className="card empty-state">
          <p>No contacts to display</p>
          <Link to="/contacts" className="btn btn-primary mt-3">
            Add Contacts
          </Link>
        </div>
      ) : (
        <div className="network-layout">
          {/* Network Canvas */}
          <div className="card network-canvas-card">
            {/* Controls */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              padding: '0.75rem',
              borderBottom: '1px solid var(--gray-200)',
              background: 'var(--gray-50)'
            }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setZoom(z => Math.min(z + 0.2, 2))}
              >
                <ZoomIn size={16} />
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))}
              >
                <ZoomOut size={16} />
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setZoom(1)}
              >
                <Maximize2 size={16} />
              </button>
              <span className="text-sm text-muted" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
                {Math.round(zoom * 100)}%
              </span>
            </div>

            {/* Canvas */}
            <div
              ref={containerRef}
              className="network-canvas"
              style={{
                cursor: dragging ? 'grabbing' : 'default'
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <svg
                width="100%"
                height="100%"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center center'
                }}
              >
                {/* Connection lines */}
                {network.connections.map(connection => {
                  const pos1 = positions[connection.contact_id_1];
                  const pos2 = positions[connection.contact_id_2];
                  if (!pos1 || !pos2) return null;

                  const isHighlighted = selected &&
                    (connection.contact_id_1 === selected || connection.contact_id_2 === selected);

                  return (
                    <line
                      key={connection.id}
                      x1={pos1.x}
                      y1={pos1.y}
                      x2={pos2.x}
                      y2={pos2.y}
                      stroke={isHighlighted ? 'var(--primary)' : 'var(--gray-300)'}
                      strokeWidth={isHighlighted ? 2 : 1}
                      strokeDasharray={isHighlighted ? 'none' : '4'}
                    />
                  );
                })}

                {/* Contact nodes */}
                {network.contacts.map(contact => {
                  const pos = positions[contact.id];
                  if (!pos) return null;

                  const isSelected = selected === contact.id;
                  const isConnected = selectedConnections.some(
                    c => c.contact_id_1 === contact.id || c.contact_id_2 === contact.id
                  );
                  const nodeSize = 20 + (contact.relationship_strength / 10);

                  return (
                    <g
                      key={contact.id}
                      transform={`translate(${pos.x}, ${pos.y})`}
                      onMouseDown={(e) => handleMouseDown(e, contact.id)}
                      style={{ cursor: 'grab' }}
                    >
                      <circle
                        r={nodeSize}
                        fill={getCategoryColor(contact.category)}
                        opacity={selected && !isSelected && !isConnected ? 0.3 : 1}
                        stroke={isSelected ? 'var(--gray-900)' : 'white'}
                        strokeWidth={isSelected ? 3 : 2}
                      />
                      <text
                        y={nodeSize + 15}
                        textAnchor="middle"
                        fontSize="12"
                        fill="var(--gray-700)"
                        opacity={selected && !isSelected && !isConnected ? 0.3 : 1}
                      >
                        {contact.first_name}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Sidebar */}
          <div className="network-sidebar">
            {/* Legend */}
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '1rem' }}>Legend</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {['family', 'friends', 'professional', 'acquaintance'].map(category => (
                  <div key={category} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      background: getCategoryColor(category)
                    }} />
                    <span style={{ textTransform: 'capitalize' }}>{category}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted mt-3">
                Node size indicates relationship strength
              </div>
            </div>

            {/* Selected Contact */}
            {selectedContact ? (
              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '1rem' }}>Selected</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div
                    className="avatar"
                    style={{ background: getCategoryColor(selectedContact.category) }}
                  >
                    {selectedContact.first_name[0]}{selectedContact.last_name?.[0] || ''}
                  </div>
                  <div>
                    <div className="font-medium">
                      {selectedContact.first_name} {selectedContact.last_name}
                    </div>
                    <div className="text-sm text-muted" style={{ textTransform: 'capitalize' }}>
                      {selectedContact.category}
                    </div>
                  </div>
                </div>
                <div className="text-sm" style={{ marginBottom: '0.5rem' }}>
                  <span className="text-muted">Strength:</span> {selectedContact.relationship_strength}%
                </div>
                <div className="text-sm" style={{ marginBottom: '1rem' }}>
                  <span className="text-muted">Connections:</span> {selectedConnections.length}
                </div>
                <Link
                  to={`/contacts/${selectedContact.id}`}
                  className="btn btn-primary btn-sm"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  View Profile
                </Link>
              </div>
            ) : (
              <div className="card">
                <div className="text-sm text-muted text-center" style={{ padding: '1rem' }}>
                  Click on a contact to see details
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '1rem' }}>Network Stats</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-muted">Contacts</span>
                  <span className="font-medium">{network.contacts.length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-muted">Connections</span>
                  <span className="font-medium">{network.connections.length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-muted">Avg. Strength</span>
                  <span className="font-medium">
                    {network.contacts.length > 0
                      ? Math.round(network.contacts.reduce((sum, c) => sum + c.relationship_strength, 0) / network.contacts.length)
                      : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .network-layout {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 1.5rem;
        }
        .network-canvas-card {
          padding: 0;
          overflow: hidden;
        }
        .network-canvas {
          width: 100%;
          height: 500px;
          position: relative;
          overflow: hidden;
        }
        .network-sidebar {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        @media (max-width: 768px) {
          .network-layout {
            grid-template-columns: 1fr;
          }
          .network-canvas {
            height: 350px;
          }
          .network-sidebar {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }
        }
        @media (max-width: 480px) {
          .network-canvas {
            height: 300px;
          }
          .network-sidebar {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default Network;
